import React, { useState, useMemo } from 'react';
import styles from './BoomBassadors.module.scss';
import { ArrowLeft } from 'react-feather';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import {
    GET_STUDENTS_FROM_CLASS,
    UPDATE_BOOMBASSADOR_SCHOOL_STATUS,
    GET_SCHOOL_BOOMBASSADORS,
    FIND_STUDENTS_BY_NAME,
} from './graphql';
import { useEffect } from 'react';
import Boomer from '../../../../../images/loader-boomer.png';
import { Button } from '@squirrellearningltd/go-teacher-components';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';

const BoomBassadorsAdd = ({ schoolId, availableClasses, setCurrentView }) => {
    const [selectedClass, setSelectedClass] = useState('');
    const [students, setStudents] = useState([]);
    const [visibleStudents, setVisibleStudents] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const { t } = useTranslation(['classes', 'common']);

    const [
        getStudentsFromClass,
        { loading: studentsClassLoading, error: studentsFromClassError },
    ] = useLazyQuery(GET_STUDENTS_FROM_CLASS, {
        onCompleted: ({ studentsClass }) => {
            setStudents(studentsClass.students);
        },
    });

    const [
        findStudents,
        { error: findStudentsError, loading: findStudentsLoading },
    ] = useLazyQuery(FIND_STUDENTS_BY_NAME, {
        fetchPolicy: 'network-only',
        onCompleted: ({ studentsByName }) => {
            setStudents(studentsByName);
        },
    });

    const [addBoomBassadors, { loading }] = useMutation(
        UPDATE_BOOMBASSADOR_SCHOOL_STATUS,
        {
            variables: {
                schoolId,
                isBoombassador: true,
                studentIds: selectedStudents,
            },
            awaitRefetchQueries: true,
            refetchQueries: () => [
                {
                    query: GET_SCHOOL_BOOMBASSADORS,
                    variables: {
                        schoolId,
                    },
                },
            ],
            onCompleted: (response) => {
                if (response?.updateBoombassadorClassStatus?.errors.length) {
                    alert(
                        'There was an error adding the BoomBassadors. Please try again.'
                    );
                    return;
                }
                alert('Boombassador successfully added');
                setSelectedStudents([]);
            },
            onError: () => {
                alert(
                    'There was an error adding the BoomBassadors. Please try again.'
                );
            },
        }
    );

    const onSelectStudent = (e) => {
        if (selectedStudents.indexOf(e.target.value) !== -1) {
            setSelectedStudents(
                selectedStudents.filter((s) => s !== e.target.value)
            );
        } else {
            setSelectedStudents([...selectedStudents, e.target.value]);
        }
    };

    useEffect(() => {
        setStudents([]);
        setSelectedStudents([]);
        if (selectedClass) {
            getStudentsFromClass({ variables: { id: selectedClass } });
        }
    }, [selectedClass]);

    useEffect(() => {
        if (students && students.length) {
            setVisibleStudents(students);
        } else {
            setVisibleStudents([]);
        }
        setSelectedStudents([]);
    }, [students]);

    const debouncedSearch = useMemo(
        () =>
            debounce((searchQuery) => {
                if (searchQuery) {
                    findStudents({
                        variables: {
                            name: searchQuery,
                            schoolId,
                        },
                    });
                } else {
                    setStudents([]);
                }
            }, 500),
        []
    );

    const onInputChange = (e) => {
        const currentSearchQuery = e.target.value;
        if (selectedClass) {
            // This one does not require debouncing as the search is done locally without querying the back-end
            setVisibleStudents(
                students.filter((student) =>
                    student.displayName
                        .toLowerCase()
                        .includes(currentSearchQuery.toLowerCase())
                )
            );
        } else {
            debouncedSearch(currentSearchQuery);
        }
    };

    return (
        <>
            <button
                onClick={() => setCurrentView('manageBoomBassadors')}
                className={`${styles.backButton} u-m-right-20`}
            >
                <ArrowLeft className={styles.backButtonIcon} />
                Back To Manage BoomBassadors
            </button>
            <div className='u-display-flex u-align-center'>
                <div style={{ width: '20%', margin: '20px 20px 10px 0' }}>
                    <select
                        className='basic-form__text-select'
                        onChange={(e) =>
                            setSelectedClass(e.currentTarget.value)
                        }
                    >
                        <option key='all' value=''>
                            All Classes
                        </option>

                        {availableClasses
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((c) => {
                                return (
                                    <option key={`class${c.id}`} value={c.id}>
                                        {c.name}
                                    </option>
                                );
                            })}
                    </select>
                </div>
                <div style={{ width: '300px' }}>
                    <input
                        type='text'
                        className='basic-form__text-box u-m-right-2 u-m-base-0 '
                        placeholder={t('search_by_name')}
                        onChange={(e) => onInputChange(e)}
                    />
                </div>
            </div>

            {studentsClassLoading && <p>Loading...</p>}
            {studentsFromClassError && <p>{studentsFromClassError.message}</p>}

            {findStudentsLoading && <p>Finding students...</p>}
            {findStudentsError && <p>{findStudentsError.message}</p>}

            {visibleStudents && visibleStudents.length ? (
                <>
                    <div className={styles.boomBassadorTileList}>
                        {visibleStudents.map((student, index) => (
                            <div
                                className={styles.boomBassadorTile}
                                key={student.id + index}
                            >
                                <div className='basic-form__group--check'>
                                    <input
                                        name='student'
                                        className='basic-form__check-box'
                                        type='checkbox'
                                        value={student.id}
                                        onChange={(e) => onSelectStudent(e)}
                                        checked={
                                            selectedStudents.indexOf(
                                                student.id
                                            ) > -1
                                        }
                                        id={`st_${student.id}`}
                                    />
                                    <label
                                        className='basic-form__check-label'
                                        htmlFor={`st_${student.id}`}
                                    >
                                        &nbsp;
                                    </label>
                                </div>
                                <div
                                    className={
                                        styles.boomBassadorTileNameAndImage
                                    }
                                >
                                    <img
                                        src={
                                            student.boomerAvatarUrl
                                                ? `${
                                                      student.boomerAvatarUrl
                                                  }?v=${student.updatedAt.replace(
                                                      /\s/g,
                                                      ''
                                                  )}`
                                                : Boomer
                                        }
                                        alt='student-image'
                                        style={{
                                            width: '80px',
                                            height: 'auto',
                                            margin: '0 auto',
                                        }}
                                    />
                                    <div>{student.displayName}</div>
                                    <div>{student?.classes[0]?.name}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <p>No available Students</p>
            )}
            <div className={styles.bottomBanner}>
                <div className={styles.addBoomBassadorButton}>
                    <Button
                        disabled={loading || !selectedStudents.length}
                        onClick={() => addBoomBassadors()}
                        className={styles.addBoomBassador}
                    >
                        Add
                    </Button>
                </div>
            </div>
        </>
    );
};

export default BoomBassadorsAdd;
